import { useState, useMemo, useRef, useEffect } from "react";
import {
    Box,
    Heading,
    VStack,
    HStack,
    Text,
    Button,
    Progress,
    Spacer,
    Grid,
    GridItem,
} from "@chakra-ui/react";
import { allCodingQuizData } from "../../data/allCourseQuizData";
import { AccessibleCodeEditor } from "../AccessibleCodeEditor";
import { useColorModeValue } from "../ui/color-mode";
import { useAccessibility } from "../../context/AccessibilityContext";
import correctSound from "../../assets/sounds/correct.mp3";
import wrongSound from "../../assets/sounds/wrong.mp3";
import navigationSound from "../../assets/sounds/navigation.mp3";

/**
 * CodingQuizScreen - Code-based quiz interface
 * Allows users to write and execute Python code to solve challenges
 */
export const CodingQuizScreen = ({ courseId = "python", onNavigateToAccessibility, userName, onNext, onBack }) => {
    const courseName = courseId === "javascript" ? "JavaScript" : courseId === "react" ? "React" : courseId === "html" ? "HTML" : courseId === "css" ? "CSS" : "Python";
    const codingQuizData = allCodingQuizData[courseId] || allCodingQuizData["python"];
    const [selectedModule, setSelectedModule] = useState(codingQuizData[0]);
    const questions = useMemo(() => selectedModule.questions, [selectedModule]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [userCode, setUserCode] = useState({});
    const [output, setOutput] = useState("");
    const [error, setError] = useState("");
    const [showResults, setShowResults] = useState(false);
    const [results, setResults] = useState({});
    const { soundEnabled } = useAccessibility();

    const moduleListRef = useRef(null);
    const questionHeadingRef = useRef(null);
    const resultsRef = useRef(null);

    const cardBg = useColorModeValue("white", "gray.800");
    const headingColor = useColorModeValue("gray.700", "white");
    const textColor = useColorModeValue("gray.600", "gray.400");
    const progressBg = useColorModeValue("gray.200", "gray.600");

    const currentQuestion = questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

    // Initialize user code with starter code
    useEffect(() => {
        if (currentQuestion && !userCode[currentQuestion.id]) {
            setUserCode((prev) => ({
                ...prev,
                [currentQuestion.id]: currentQuestion.starterCode || "",
            }));
        }
    }, [currentQuestion, userCode]);

    const playSound = (sound) => {
        if (soundEnabled) {
            new Audio(sound).play();
        }
    };

    const handleModuleSelect = (module) => {
        setSelectedModule(module);
        setCurrentQuestionIndex(0);
        setOutput("");
        setError("");
    };

    const handleModuleKeyDown = (e) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault(); // Prevent page scroll
            const currentIndex = codingQuizData.findIndex(m => m.module === selectedModule.module);
            let nextIndex = e.key === "ArrowDown" ? currentIndex + 1 : currentIndex - 1;
            
            if (nextIndex >= 0 && nextIndex < codingQuizData.length) {
                handleModuleSelect(codingQuizData[nextIndex]);
                playSound(navigationSound); // Provide audible feedback
            }
        } else if (e.key === "ArrowRight") {
            // Jump focus to the main question/editor area
            e.preventDefault();
            questionHeadingRef.current?.focus();
            playSound(navigationSound);
        }
    };

    const handleCodeChange = (code) => {
        setUserCode((prev) => ({
            ...prev,
            [currentQuestion.id]: code,
        }));
        setOutput("");
        setError("");
    };

    const handleRunCode = () => {
        const code = userCode[currentQuestion.id] || "";

        // Simple code validation (in a real app, this would run on a backend)
        try {
            // Basic syntax check
            if (!code.trim()) {
                setError("Please write some code first!");
                playSound(wrongSound);
                return;
            }

            // Simulate code execution (this is a simplified version)
            // In production, you'd send this to a backend Python interpreter
            const simulatedOutput = simulateCodeExecution(code, currentQuestion);

            if (simulatedOutput.error) {
                setError(simulatedOutput.error);
                playSound(wrongSound);
                setResults((prev) => ({
                    ...prev,
                    [currentQuestion.id]: { passed: false, output: simulatedOutput.error },
                }));
            } else {
                setOutput(simulatedOutput.output);
                const passed = checkCodeCorrectness(simulatedOutput.output, currentQuestion);

                if (passed) {
                    playSound(correctSound);
                    setResults((prev) => ({
                        ...prev,
                        [currentQuestion.id]: { passed: true, output: simulatedOutput.output },
                    }));
                } else {
                    playSound(wrongSound);
                    setResults((prev) => ({
                        ...prev,
                        [currentQuestion.id]: { passed: false, output: simulatedOutput.output },
                    }));
                }
            }
        } catch (err) {
            setError(err.message);
            playSound(wrongSound);
        }
    };

    // Simplified code execution simulator
    const simulateCodeExecution = (code, question) => {
        try {
            // Check for common Python syntax patterns
            if (code.includes("print(") && question.expectedOutput) {
                // Extract what's being printed (very simplified)
                const printMatch = code.match(/print\((.*?)\)/);
                if (printMatch) {
                    let output = printMatch[1].trim();
                    // Remove quotes if present
                    output = output.replace(/['"]/g, "");
                    return { output };
                }
            }

            // For questions without specific output, just confirm code was written
            return {
                output: `Code executed successfully! (Note: This is a simulation. In production, code would run on a real ${courseName} interpreter.)`
            };
        } catch (err) {
            return { error: "Syntax Error: " + err.message };
        }
    };

    const checkCodeCorrectness = (output, question) => {
        if (!question.expectedOutput) {
            // For questions without specific expected output, check if code contains required elements
            const code = userCode[question.id] || "";

            // Basic checks based on question ID
            if (question.id.includes("c1-2")) {
                return code.includes("name") && code.includes("print");
            }

            return true; // Accept any code for now
        }

        return output.trim() === question.expectedOutput.trim();
    };

    const handleNext = () => {
        playSound(navigationSound);
        setCurrentQuestionIndex((prev) => Math.min(prev + 1, questions.length - 1));
        setOutput("");
        setError("");
        setTimeout(() => {
            questionHeadingRef.current?.focus();
        }, 0);
    };

    const handleBack = () => {
        playSound(navigationSound);
        setCurrentQuestionIndex((prev) => Math.max(prev - 1, 0));
        setOutput("");
        setError("");
    };

    const handleSubmit = () => {
        playSound(navigationSound);
        setShowResults(true);
    };

    const handleRestart = () => {
        setCurrentQuestionIndex(0);
        setUserCode({});
        setOutput("");
        setError("");
        setResults({});
        setShowResults(false);
    };

    const calculateScore = () => {
        const passedCount = Object.values(results).filter((r) => r.passed).length;
        return passedCount;
    };

    if (showResults) {
        const score = calculateScore();

        return (
            <Box p={8} width="100%">
                <Grid
                    templateColumns={{ base: "1fr", lg: "1fr 2.5fr" }}
                    gap={8}
                    width="100%"
                    maxW="1600px"
                    mx="auto"
                >
                    <GridItem>
                        <Box p={6} bg={cardBg} borderRadius="lg" boxShadow="xl" height="100%">
                            <VStack spacing={4} align="stretch">
                                <Heading size="lg" color={headingColor} mb={4}>
                                    {courseName} Modules
                                </Heading>
                                {codingQuizData.map((module) => (
                                    <Button
                                        key={module.module}
                                        onClick={() => handleModuleSelect(module)}
                                        colorScheme={selectedModule.module === module.module ? "blue" : "gray"}
                                        variant={selectedModule.module === module.module ? "solid" : "outline"}
                                        justifyContent="flex-start"
                                    >
                                        {module.module}
                                    </Button>
                                ))}
                            </VStack>
                        </Box>
                    </GridItem>

                    <GridItem>
                        <Box
                            p={8}
                            bg={cardBg}
                            borderRadius="lg"
                            boxShadow="xl"
                            ref={resultsRef}
                            tabIndex={0}
                            role="region"
                            aria-label="Coding quiz results"
                        >
                            <VStack spacing={8}>
                                <Box textAlign="center" width="100%">
                                    <Heading size="2xl" color={headingColor} mb={4}>
                                        Coding Challenge Complete!
                                    </Heading>
                                    <Text fontSize="xl" color={textColor} fontWeight="medium">
                                        You completed{" "}
                                        <Text as="span" color="blue.500" fontWeight="bold">
                                            {score}
                                        </Text>{" "}
                                        out of{" "}
                                        <Text as="span" fontWeight="bold">
                                            {questions.length}
                                        </Text>{" "}
                                        challenges successfully
                                    </Text>
                                </Box>

                                <HStack spacing={4} justify="center">
                                    <Button onClick={handleRestart} colorScheme="blue" size="lg">
                                        Try Again
                                    </Button>
                                    <Button onClick={onNext} colorScheme="purple" size="lg">
                                        Next to Feedback Screen
                                    </Button>
                                    <Button onClick={onBack} variant="outline" size="lg">
                                        Back to Quiz Selection
                                    </Button>
                                </HStack>

                                <VStack spacing={6} align="stretch" width="100%">
                                    <Heading size="lg" color={headingColor} textAlign="center">
                                        Review Your Solutions
                                    </Heading>
                                    {questions.map((question) => {
                                        const result = results[question.id];
                                        const code = userCode[question.id] || "No code submitted";

                                        return (
                                            <Box
                                                key={question.id}
                                                p={6}
                                                bg={cardBg}
                                                borderRadius="lg"
                                                boxShadow="md"
                                                borderWidth="2px"
                                                borderColor={result?.passed ? "green.500" : "red.500"}
                                            >
                                                <Heading size="md" mb={4} color={headingColor}>
                                                    {question.question}
                                                </Heading>
                                                <Text fontSize="sm" color={textColor} mb={4}>
                                                    {result?.passed ? "✅ Passed" : "❌ Failed or Not Attempted"}
                                                </Text>

                                                <Box mb={4}>
                                                    <Text fontWeight="bold" mb={2}>Your Code:</Text>
                                                    <Box
                                                        as="pre"
                                                        p={4}
                                                        bg={progressBg}
                                                        borderRadius="md"
                                                        fontFamily="monospace"
                                                        fontSize="sm"
                                                        overflow="auto"
                                                    >
                                                        {code}
                                                    </Box>
                                                </Box>

                                                <Box>
                                                    <Text fontWeight="bold" mb={2}>Expected Solution:</Text>
                                                    <Box
                                                        as="pre"
                                                        p={4}
                                                        bg={progressBg}
                                                        borderRadius="md"
                                                        fontFamily="monospace"
                                                        fontSize="sm"
                                                        overflow="auto"
                                                    >
                                                        {question.solution}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </VStack>
                            </VStack>
                        </Box>
                    </GridItem>
                </Grid>
            </Box>
        );
    }

    return (
        <Box p={{ base: 2, md: 8 }} width="100%">
            <Grid
                templateColumns={{ base: "1fr", lg: "1fr 2.5fr" }}
                gap={8}
                width="100%"
                maxW="1600px"
                mx="auto"
            >
                <GridItem>
                    <Box p={6} bg={cardBg} borderRadius="lg" boxShadow="xl" height="100%">
                        <VStack
                            spacing={4}
                            align="stretch"
                            tabIndex={0}
                            role="listbox"
                            aria-label="Coding quiz modules"
                            ref={moduleListRef}
                            onKeyDown={handleModuleKeyDown}
                        >
                            <Heading size="lg" color={headingColor} mb={2}>
                                {courseName} Modules
                            </Heading>
                            <Text fontSize="sm" color={textColor} mb={4} fontStyle="italic" aria-hidden="true" id="module-nav-instructions">
                                Use ↑/↓ to change modules, and → to jump to the Code Editor.
                            </Text>
                            {codingQuizData.map((module) => (
                                <Button
                                    key={module.module}
                                    onClick={() => handleModuleSelect(module)}
                                    colorScheme={selectedModule.module === module.module ? "blue" : "gray"}
                                    variant={selectedModule.module === module.module ? "solid" : "outline"}
                                    justifyContent="flex-start"
                                    role="option"
                                    aria-selected={selectedModule.module === module.module}
                                >
                                    {module.module}
                                </Button>
                            ))}
                        </VStack>
                    </Box>
                </GridItem>

                <GridItem>
                    <Box p={8} bg={cardBg} borderRadius="lg" boxShadow="2xl" ref={questionHeadingRef} tabIndex={-1}>
                        <VStack spacing={8} align="stretch">
                            <Box>
                                <HStack justify="space-between" mb={2}>
                                    <Text fontWeight="bold" color={textColor}>
                                        Challenge {currentQuestionIndex + 1} of {questions.length}
                                    </Text>
                                    <Text fontWeight="bold" color={textColor}>
                                        {Math.round(progress)}% Complete
                                    </Text>
                                </HStack>
                                <Progress.Root
                                    value={progress}
                                    size="sm"
                                    colorScheme="blue"
                                    bg={progressBg}
                                    borderRadius="md"
                                >
                                    <Progress.Track>
                                        <Progress.Range />
                                    </Progress.Track>
                                </Progress.Root>
                            </Box>

                            <AccessibleCodeEditor
                                value={userCode[currentQuestion.id] || ""}
                                onChange={handleCodeChange}
                                onRun={handleRunCode}
                                output={output}
                                error={error}
                                hint={currentQuestion.hint}
                                ariaLabel={currentQuestion.ariaLabel}
                                question={currentQuestion.question}
                                description={currentQuestion.description}
                            />

                            <HStack justify="space-between" mt={8} wrap="wrap" gap={2}>
                                <HStack>
                                    <Button onClick={onNavigateToAccessibility} variant="outline">
                                        Accessibility
                                    </Button>
                                    <Button onClick={onBack} variant="outline" colorScheme="purple">
                                        Back to Quiz Selection
                                    </Button>
                                </HStack>
                                <Spacer />
                                <HStack>
                                    <Button onClick={handleBack} isDisabled={currentQuestionIndex === 0}>
                                        Go Back to Previous Challenge
                                    </Button>
                                    {currentQuestionIndex < questions.length - 1 && (
                                        <Button onClick={handleNext} colorScheme="blue">
                                            Go to Next Challenge
                                        </Button>
                                    )}
                                </HStack>
                            </HStack>

                            <Button
                                onClick={handleSubmit}
                                colorScheme="blue"
                                width="full"
                                size="lg"
                            >
                                Submit All Challenges
                            </Button>
                        </VStack>
                    </Box>
                </GridItem>
            </Grid>
        </Box>
    );
};
